import { atom, atomFamily, selectorFamily, selector } from 'recoil'
import { setDefaultService } from 'utils'
import { ensureDefaultService } from '@/utils/caseDetails/ensureDefaultService'
import { default as _set } from 'lodash/set'
import { default as _get } from 'lodash/get'
import { produce } from 'immer'
import { parseNestedField } from '@/utils/atoms/parseNestedField'

export const delayNestedObjectInputAtom = atom({
  key: 'delayNestedObjectInput',
  default: false,
})

// Pending additional-family entry ({ name, relation, editingIndex }) typed
// into the flat input but not yet added — flushed into additional_family by
// the saveCase paths so a plain Save persists it.
export const additionalFamilyDraftAtom = atom({
  key: 'additionalFamilyDraft',
  default: null,
})

export const caseFieldsAtom = atom({
  key: 'caseFields',
  default: [],
})

export const caseValuesAtom = atomFamily({
  key: 'caseValues',
  default: {},
})

export const caseAtom = atom({
  key: 'case',
  default: {},
})

/**
 * caseSelector derives default_service and default_visitation from the services array.
 * Allows TWO defaults: one service with type="service" and one with type="visitation".
 */
export const caseSelector = selector({
  key: 'caseSelector',
  get: ({ get }) => {
    const _case = get(caseAtom)
    if (!_case) return null

    const services = _case?.services || []
    const newCase = { ..._case }

    // Derive default_service (type === 'service' with default === true)
    if (!_case?.default_service) {
      const defaultService = services.find(
        (s) => s.default && s.type?.toLowerCase() === 'service',
      )
      newCase.default_service = defaultService || null
    }

    // Derive default_visitation (type === 'visitation' with default === true)
    if (!_case?.default_visitation) {
      const defaultVisitation = services.find(
        (s) => s.default && s.type?.toLowerCase() === 'visitation',
      )
      newCase.default_visitation = defaultVisitation || null
    }

    return newCase
  },
  set: ({ set }, newValue) => {
    if (!newValue) return null

    // Normalize services: ensure exactly one default per type
    // This handles bad data from API (e.g., multiple defaults of same type)
    const normalizedServices = ensureDefaultService(newValue.services || [])
    newValue.services = normalizedServices

    // Derive default_service from normalized services
    if (!newValue.default_service) {
      newValue.default_service =
        normalizedServices.find(
          (s) => s.default && s.type?.toLowerCase() === 'service',
        ) || null
    }

    // Derive default_visitation from normalized services
    if (!newValue.default_visitation) {
      newValue.default_visitation =
        normalizedServices.find(
          (s) => s.default && s.type?.toLowerCase() === 'visitation',
        ) || null
    }

    set(caseAtom, newValue)
  },
})

export const editCaseFieldSelector = selectorFamily({
  key: 'editCaseField',
  get:
    ({ name, path }) =>
    ({ get }) => {
      // Handle nested objects in the case object
      const nestedInfo = parseNestedField(name, get)
      if (nestedInfo) {
        const { parentAtom, fieldPath } = nestedInfo
        if (!parentAtom?.value) return null
        return _get(parentAtom.value, fieldPath)
      }

      const fieldValue = get(caseValuesAtom(name))
      if (!fieldValue) return null
      if (!fieldValue.value) return null
      const value = _get(fieldValue, path)
      const isEmptyString = value === ''
      const shouldResetDate =
        (name.includes('date') || path.includes('date')) && !isEmptyString
      if (shouldResetDate) return new Date(value)
      return value
    },
  set:
    ({ name, path }) =>
    ({ set, get }, newValue) => {
      // Handle nested objects in the case object
      const nestedInfo = parseNestedField(name, get)
      if (nestedInfo) {
        const { parentKey, fieldPath } = nestedInfo
        let { parentAtom } = nestedInfo

        if (!parentAtom) {
          parentAtom = { id: parentKey, value: {} }
        }

        const newParentValue = produce(parentAtom, (draft) => {
          const actualPath = `value.${fieldPath}`
          _set(draft, actualPath, newValue)
        })

        set(caseValuesAtom(parentKey), newParentValue)
        return
      }

      const fieldValue = get(caseValuesAtom(name))
      if (!fieldValue) return null

      const setCaseDetail = () => {
        const newField = produce(fieldValue, (draft) =>
          _set(draft, path, newValue),
        )
        set(caseValuesAtom(name), newField)
      }

      const variables = {
        name,
        path,
        set,
        get,
        newValue,
        fieldValue,
        caseValuesAtom,
        setCaseDetail,
      }

      setDefaultService(variables)
    },
})
